import { useCallback } from 'react';

import { useAgentVisibility } from '@/entities/agent';
import { useCurrentUser, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';

export interface ManagedToolAccess {
  /** Whether the caller holds the tool's open permission AND its workspace allows it. */
  canOpen: (toolId: string) => boolean;
  isLoading: boolean;
}

// Each id must map to an open permission in AGENT_TOOL_OPEN_PERMISSIONS and appear in the
// backend's managed-tool ACL ids. A permission alone admits a user into a workspace the tool
// was never switched on for, and the ACL alone shows it to users who may not open it.
const ACCESS_GATED_TOOL_IDS = ['test-content', 'nacb-intake', 'focus-groups'];

/**
 * The single gate for the managed tools that need both a permission and a per-workspace ACL
 * allow, so the tools grid and the top-nav dropdown cannot drift into disagreeing conditions.
 * IQ and Content Studio carry extra conditions of their own and keep their dedicated hooks.
 */
export const useManagedToolAccess = (): ManagedToolAccess => {
  const { canOpenTool } = usePermissions();
  const workspaceId = useCurrentWorkspaceId();
  // Non-throwing read: this feeds surfaces that mount before the user store fills. The
  // fallback never reaches a request, since a workspace only resolves once that store has a user.
  const organizationId = useCurrentUser()?.organization.id;
  const { isAgentEnabled, isLoading } = useAgentVisibility(ACCESS_GATED_TOOL_IDS, {
    workspaceId,
    organizationId: organizationId ?? '',
  });

  const canOpen = useCallback(
    (toolId: string) => canOpenTool(toolId) && isAgentEnabled(toolId),
    [canOpenTool, isAgentEnabled],
  );

  return { canOpen, isLoading };
};
